import { forwardRef } from 'react';
import { cn } from '../utils/cn';
import { Badge, type BadgeTone } from '../primitives/Badge';
import { Button } from '../primitives/Button';
import { Icon, type IconName } from '../primitives/Icon';
import { formatTimestamp } from '../utils/format';

export type JobStatus = 'Queued' | 'Running' | 'Completed' | 'Warning' | 'Failed' | 'Cancelled';

const statusMeta: Record<JobStatus, { icon: IconName; tone: BadgeTone; bar: string }> = {
  Queued: { icon: 'clock', tone: 'neutral', bar: 'bg-[var(--color-border-strong)]' },
  Running: { icon: 'refresh', tone: 'info', bar: 'bg-action' },
  Completed: { icon: 'check-circle', tone: 'success', bar: 'bg-success' },
  Warning: { icon: 'alert-circle', tone: 'warning', bar: 'bg-[var(--color-warning)]' },
  Failed: { icon: 'x-circle', tone: 'danger', bar: 'bg-danger' },
  Cancelled: { icon: 'x', tone: 'neutral', bar: 'bg-[var(--color-border-strong)]' },
};

export interface JobCounts {
  submitted?: number;
  read?: number;
  valid?: number;
  warning?: number;
  rejected?: number;
  unchanged?: number;
  created?: number;
  updated?: number;
}

export interface JobProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  jobLabel: string;
  runCode?: string | null;
  status: JobStatus;
  /** 0–100. Omit for an indeterminate run. */
  percentComplete?: number | null;
  currentStep?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  counts?: JobCounts;
  /** Why a run failed or warned; shown verbatim. */
  message?: string | null;
  onCancel?: () => void;
  onViewDetails?: () => void;
  density?: 'comfortable' | 'compact';
}

const countLabels: Array<[keyof JobCounts, string]> = [
  ['submitted', 'Submitted'],
  ['read', 'Read'],
  ['valid', 'Valid'],
  ['warning', 'Warnings'],
  ['rejected', 'Rejected'],
  ['created', 'Created'],
  ['updated', 'Updated'],
  ['unchanged', 'Unchanged'],
];

export const JobProgress = forwardRef<HTMLDivElement, JobProgressProps>(function JobProgress(
  {
    jobLabel,
    runCode,
    status,
    percentComplete,
    currentStep,
    startedAt,
    finishedAt,
    counts,
    message,
    onCancel,
    onViewDetails,
    density = 'comfortable',
    className,
    ...rest
  },
  ref,
) {
  const meta = statusMeta[status];
  const indeterminate =
    status === 'Running' && (percentComplete === null || percentComplete === undefined);

  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col gap-3 rounded-xl border border-line bg-surface shadow-card',
        density === 'compact' ? 'p-3' : 'p-4',
        className,
      )}
      {...rest}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="truncate text-body font-semibold text-content">{jobLabel}</p>
          <p className="text-dense text-content-muted">
            {runCode ? `${runCode} · ` : ''}
            Started {formatTimestamp(startedAt ?? null)}
            {finishedAt ? ` · finished ${formatTimestamp(finishedAt)}` : ''}
          </p>
        </div>
        <Badge tone={meta.tone} icon={meta.icon}>
          {status}
        </Badge>
      </div>

      <div className="flex flex-col gap-1.5">
        <div
          role="progressbar"
          aria-label={`${jobLabel} progress`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={indeterminate ? undefined : Math.round(percentComplete ?? 0)}
          aria-valuetext={indeterminate ? 'In progress' : undefined}
          className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
        >
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-base ease-standard',
              meta.bar,
              indeterminate && 'w-1/3 animate-pvi-shimmer',
            )}
            style={indeterminate ? undefined : { width: `${Math.min(percentComplete ?? 0, 100)}%` }}
          />
        </div>
        <p className="flex items-center justify-between gap-2 text-dense text-content-secondary">
          <span className="truncate">{currentStep ?? statusSentence(status)}</span>
          {!indeterminate && percentComplete !== null && percentComplete !== undefined ? (
            <span className="tabular-nums">{Math.round(percentComplete)}%</span>
          ) : null}
        </p>
      </div>

      {counts ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 stack:grid-cols-4">
          {countLabels
            .filter(([key]) => counts[key] !== undefined)
            .map(([key, label]) => (
              <div key={key} className="flex items-baseline justify-between gap-2">
                <dt className="text-dense text-content-muted">{label}</dt>
                <dd
                  className={cn(
                    'text-dense-lg font-semibold tabular-nums text-content',
                    key === 'rejected' && (counts[key] ?? 0) > 0 && 'text-danger',
                    key === 'warning' && (counts[key] ?? 0) > 0 && 'text-[var(--color-warning)]',
                  )}
                >
                  {(counts[key] ?? 0).toLocaleString('en-US')}
                </dd>
              </div>
            ))}
        </dl>
      ) : null}

      {message ? (
        <p
          className={cn(
            'flex items-start gap-1.5 rounded-md border px-2.5 py-2 text-dense',
            status === 'Failed'
              ? 'border-danger-subtle bg-danger-subtle text-danger'
              : 'border-line bg-surface-sunken text-content-secondary',
          )}
        >
          <Icon name={status === 'Failed' ? 'alert' : 'info'} size={13} className="mt-0.5" />
          {message}
        </p>
      ) : null}

      {onCancel || onViewDetails ? (
        <div className="flex items-center justify-end gap-2">
          {onCancel && (status === 'Running' || status === 'Queued') ? (
            <Button size="sm" variant="secondary" onClick={onCancel}>
              Cancel run
            </Button>
          ) : null}
          {onViewDetails ? (
            <Button size="sm" variant="ghost" iconEnd="arrow-right" onClick={onViewDetails}>
              Run details
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});

function statusSentence(status: JobStatus): string {
  switch (status) {
    case 'Queued':
      return 'Waiting for a worker';
    case 'Running':
      return 'In progress';
    case 'Completed':
      return 'Finished without issues';
    case 'Warning':
      return 'Finished with warnings that need review';
    case 'Failed':
      return 'Run failed — no data was published';
    case 'Cancelled':
      return 'Cancelled before completion';
  }
}
